import { Helmet } from 'react-helmet-async';
import { H1, H2, H3, Body } from '../components/ui/Typography';

export default function Privacy() {
  return (
    <>
      <Helmet>
        <title>RentFi | Privacy Policy</title>
        <meta 
          name="description" 
          content="Learn about RentFi's privacy practices, how we collect, use, and protect your personal information, and your privacy rights as a user of our real estate investment platform." 
        />
        <meta 
          name="keywords" 
          content="RentFi privacy policy, data protection, personal information, privacy rights, investor privacy" 
        />
        <link rel="canonical" href="https://rentfi.io/privacy" />
      </Helmet>
      <div className="min-h-screen bg-gray-50 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <H1 className="text-primary mb-4">Privacy Policy</H1>
            <p className="text-gray-500 mb-8">Last Updated: January 30, 2025</p>

            {/* Introduction */}
            <div className="mb-8">
              <H2 className="text-primary mb-4">1. Introduction</H2>
              <Body className="text-gray-600">
                RentFi ("Company", "we", "our", "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform.
              </Body>
            </div>

            {/* Information We Collect */}
            <div className="mb-8">
              <H2 className="text-primary mb-4">2. Information We Collect</H2>
              
              <div className="space-y-6">
                <div>
                  <H3 className="text-gray-900 mb-2">2.1 Personal Information</H3>
                  <ul className="list-disc pl-6 space-y-2 text-gray-600">
                    <li>Wallet addresses</li>
                    <li>Transaction history</li>
                    <li>Token holdings</li>
                    <li>Distribution records</li>
                    <li>Usage data and analytics</li>
                  </ul>
                </div>

                <div>
                  <H3 className="text-gray-900 mb-2">2.2 Technical Information</H3>
                  <ul className="list-disc pl-6 space-y-2 text-gray-600">
                    <li>Device information</li>
                    <li>IP addresses</li>
                    <li>Browser type</li>
                    <li>Operating system</li>
                    <li>Access timestamps</li>
                    <li>Network data</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* How We Use Your Information */}
            <div className="mb-8">
              <H2 className="text-primary mb-4">3. How We Use Your Information</H2>
              <Body className="text-gray-600 mb-4">We use collected information to:</Body>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Process token distributions</li>
                <li>Verify eligibility for rewards</li>
                <li>Maintain platform security</li>
                <li>Improve our services</li>
                <li>Comply with legal obligations</li>
                <li>Communicate platform updates</li>
              </ul>
            </div>

            {/* Information Storage and Security */}
            <div className="mb-8">
              <H2 className="text-primary mb-4">4. Information Storage and Security</H2>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Data is stored using industry-standard security measures</li>
                <li>Blockchain transactions are public by nature</li>
                <li>We implement appropriate technical safeguards</li>
                <li>Third-party service providers may assist in data processing</li>
              </ul>
            </div>

            {/* Information Sharing */}
            <div className="mb-8">
              <H2 className="text-primary mb-4">5. Information Sharing</H2>
              <Body className="text-gray-600 mb-4">We may share information:</Body>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>With service providers</li>
                <li>To comply with legal obligations</li>
                <li>To protect our rights and safety</li>
                <li>In connection with business transactions</li>
              </ul>
            </div>

            {/* Your Rights */}
            <div className="mb-8">
              <H2 className="text-primary mb-4">6. Your Rights</H2>
              <Body className="text-gray-600 mb-4">You have the right to:</Body>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Access your personal information</li>
                <li>Request data correction</li>
                <li>Request data deletion</li>
                <li>Opt-out of communications</li>
                <li>Lodge complaints with supervisory authorities</li>
              </ul>
            </div>

            {/* Cookies and Tracking */}
            <div className="mb-8">
              <H2 className="text-primary mb-4">7. Cookies and Tracking</H2>
              <Body className="text-gray-600 mb-4">We use cookies and similar technologies to:</Body>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Maintain platform functionality</li>
                <li>Analyze usage patterns</li>
                <li>Enhance security</li>
                <li>Improve user experience</li>
              </ul>
            </div>

            {/* Changes to Privacy Policy */}
            <div className="mb-8">
              <H2 className="text-primary mb-4">8. Changes to Privacy Policy</H2>
              <Body className="text-gray-600">
                We may update this Privacy Policy periodically. Changes will be posted on our platform with an updated revision date.
              </Body>
            </div>

            {/* Contact Information */}
            <div className="mb-8">
              <H2 className="text-primary mb-4">9. Contact Information</H2>
              <Body className="text-gray-600">
                For privacy-related inquiries:<br />
                <a 
                  href="mailto:support@rentfi.helpcrunch-mail.com" 
                  className="text-secondary hover:text-secondary-dark"
                  aria-label="Email RentFi support team with privacy-related inquiries"
                >
                  support@rentfi.helpcrunch-mail.com
                </a>
              </Body>
            </div>

            {/* International Data Transfers */}
            <div>
              <H2 className="text-primary mb-4">10. International Data Transfers</H2>
              <Body className="text-gray-600">
                Your information may be transferred and processed in countries outside your residence jurisdiction.
              </Body>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}